import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { TOKENS, WA_LINK } from '../tokens';
import { ChapterTag } from './Details';

/* ─── Micro-shake animation wrapper ─── */
function ShakeWrap({ active, children }) {
  return (
    <motion.div
      animate={active ? {
        y:      [0, -10, 7, -5, 3, 0],
        rotate: [0,  1.2, -1, 0.6, 0],
        scale:  [1, 1.02, 0.99, 1.01, 1],
      } : {}}
      transition={{ duration: 0.9, delay: 0.6, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Sparkle SVG overlay ─── */
function Sparkles({ active }) {
  if (!active) return null;
  const rays = [
    { x1: '50%', y1: '28%', x2: '50%',  y2: '8%',  d: 0.7 },
    { x1: '58%', y1: '30%', x2: '70%',  y2: '12%', d: 0.8 },
    { x1: '42%', y1: '30%', x2: '30%',  y2: '12%', d: 0.8 },
    { x1: '65%', y1: '38%', x2: '80%',  y2: '28%', d: 0.9 },
    { x1: '35%', y1: '38%', x2: '20%',  y2: '28%', d: 0.9 },
    { x1: '62%', y1: '45%', x2: '78%',  y2: '42%', d: 1.0 },
    { x1: '38%', y1: '45%', x2: '22%',  y2: '42%', d: 1.0 },
  ];
  return (
    <svg
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 10,
        overflow: 'visible',
      }}
    >
      {rays.map((r, i) => (
        <motion.line
          key={i}
          x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2}
          stroke="#C8A96A"
          strokeWidth="2.5"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0] }}
          transition={{ duration: 0.65, delay: r.d, ease: 'easeOut' }}
        />
      ))}
      {/* Centre glow dot */}
      <motion.circle
        cx="50%" cy="38%"
        r="6"
        fill="#C8A96A"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.6, 0.8], opacity: [0, 0.9, 0] }}
        transition={{ duration: 0.7, delay: 0.65 }}
      />
    </svg>
  );
}

export default function Handshake() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (inView) {
      const t = setTimeout(() => setTriggered(true), 100);
      return () => clearTimeout(t);
    }
  }, [inView]);

  return (
    <section
      id="the-handshake"
      style={{
        background: '#050A1A',
        padding: 'clamp(100px, 12vw, 160px) clamp(20px, 4vw, 48px) clamp(80px, 8vw, 120px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes ambientPulse {
          0%,100% { opacity: .3; transform: translateX(-50%) scale(1); }
          50%      { opacity: .55; transform: translateX(-50%) scale(1.1); }
        }
      `}</style>

      {/* Soft ambient glow behind image */}
      <div style={{
        position: 'absolute',
        width: 500, height: 260,
        top: '12%', left: '50%',
        borderRadius: '50%',
        background: 'radial-gradient(ellipse, rgba(200,169,106,0.08) 0%, transparent 70%)',
        animation: 'ambientPulse 5s ease-in-out infinite',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* ── Handshake image with animation ── */}
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50, scale: 0.88 }}
        animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 520,
          marginBottom: 8,
        }}
      >
        <ShakeWrap active={triggered}>
          <div style={{ position: 'relative' }}>
            {/* The actual handshake image — background already removed */}
            <img
              src="/handshake.png"
              alt="Business handshake"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                /* mix-blend-mode makes any remaining bg pixels disappear on black */
                mixBlendMode: 'lighten',
                filter: 'drop-shadow(0 20px 60px rgba(200,169,106,0.2)) drop-shadow(0 8px 24px rgba(0,0,0,0.6))',
                userSelect: 'none',
                pointerEvents: 'none',
              }}
              draggable={false}
            />
            {/* Sparkle overlay */}
            <Sparkles active={triggered} />
          </div>
        </ShakeWrap>

        {/* Caption */}
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={triggered ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.8 }}
          style={{
            fontSize: '0.6rem',
            fontWeight: 500,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'rgba(200,169,106,0.55)',
            marginTop: 12,
          }}
        >
          Partnership Sealed
        </motion.p>
      </motion.div>

      {/* ── Text block ── */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
        style={{
          position: 'relative',
          zIndex: 3,
          maxWidth: 600,
          width: '100%',
          marginTop: 48,
        }}
      >
        <ChapterTag text="Chapter 06 · The Deal" />

        <h2 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(2.2rem, 4.5vw, 4rem)',
          fontWeight: 300,
          lineHeight: 1.2,
          color: '#fff',
          marginBottom: 20,
        }}>
          You've seen the property.<br />
          Now let's{' '}
          <em style={{ fontStyle: 'italic', color: TOKENS.GOLD }}>make it yours.</em>
        </h2>

        <p style={{
          fontSize: 'clamp(0.88rem, 1.5vw, 1rem)',
          fontWeight: 300,
          lineHeight: 1.85,
          color: TOKENS.MUTED,
          marginBottom: 48,
          maxWidth: 500,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          Every great deal starts with a single conversation. You've done the research, seen the location, understood the opportunity. The only thing left is the handshake — we're ready when you are.
        </p>

        <div style={{
          display: 'flex',
          gap: 14,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          <a
            href="#contact"
            onClick={e => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            style={btnGold}
            onMouseEnter={e => {
              e.currentTarget.style.background = TOKENS.GOLD_LIGHT;
              e.currentTarget.style.transform = 'scale(1.04)';
              e.currentTarget.style.boxShadow = '0 0 40px rgba(200,169,106,0.3)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = TOKENS.GOLD;
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Schedule a Site Visit
          </a>
          <a
            href={WA_LINK('Hi%2C%20I%20am%20ready%20to%20discuss%20the%20Jhandewalan%20property.')}
            target="_blank"
            rel="noreferrer"
            style={btnWa}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#1db954';
              e.currentTarget.style.transform = 'scale(1.04)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#25D366';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Let's Talk — WhatsApp
          </a>
        </div>
      </motion.div>
    </section>
  );
}

const btnGold = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '14px 28px', background: '#C8A96A', color: '#000',
  fontFamily: "'Outfit', sans-serif", fontSize: '0.78rem', fontWeight: 500,
  letterSpacing: '0.04em', borderRadius: 980, textDecoration: 'none',
  transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)', cursor: 'none',
};
const btnWa = {
  display: 'inline-flex', alignItems: 'center', gap: 10,
  padding: '14px 28px', background: '#25D366', color: '#fff',
  fontFamily: "'Outfit', sans-serif", fontSize: '0.78rem', fontWeight: 500,
  letterSpacing: '0.04em', borderRadius: 980, textDecoration: 'none',
  transition: 'all 0.3s', cursor: 'none',
};
